import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import type { ItemKind } from "@/lib/catalog-api";

export interface OptionGroup { group: string; options: { value: string; hex?: string }[]; }

export interface ItemOptions {
  clothing_sizes: { group: string; options: string[] }[];
  shoe_size_systems: Record<string, string[]>;
  default_shoe_system: string;
  shoe_size_extras: string[];
  shoe_types: string[];
  color_families: { family: string; shades: { name: string; hex: string }[] }[];
  color_extras: string[];
  materials: string[];
  fabric_types: string[];
  fabric_features: string[];
  shoe_keywords: string[];
  accessory_keywords: string[];
}

let cache: Promise<ItemOptions> | null = null;

export function useItemOptions(): ItemOptions | null {
  const [opts, setOpts] = useState<ItemOptions | null>(null);
  useEffect(() => {
    cache = cache || apiFetch<ItemOptions>("/items/options");
    cache.then(setOpts).catch(() => { cache = null; });
  }, []);
  return opts;
}

const hasWord = (text: string, words: string[]) =>
  words.some((w) => new RegExp(`\\b${w}`, "i").test(text));

export function detectKind(category: string | null | undefined, o: ItemOptions): ItemKind {
  const c = category || "";
  if (hasWord(c, o.shoe_keywords)) return "shoes";
  if (hasWord(c, o.accessory_keywords)) return "accessory";
  return "clothing";
}

export const flat = (values: string[], group = ""): OptionGroup[] =>
  [{ group, options: values.map((value) => ({ value })) }];

export function colorGroups(o: ItemOptions): OptionGroup[] {
  return [
    ...o.color_families.map((f) => ({
      group: f.family,
      options: f.shades.map((s) => ({ value: s.name, hex: s.hex || undefined })),
    })),
    { group: "Additional", options: o.color_extras.map((value) => ({ value })) },
  ];
}

export function sizeGroups(o: ItemOptions, kind: ItemKind, system?: string | null): OptionGroup[] {
  if (kind === "shoes") {
    const sys = system || o.default_shoe_system;
    return [
      { group: sys, options: (o.shoe_size_systems[sys] || []).map((value) => ({ value })) },
      { group: "Additional", options: o.shoe_size_extras.map((value) => ({ value })) },
    ];
  }
  return o.clothing_sizes.map((g) => ({ group: g.group, options: g.options.map((value) => ({ value })) }));
}

export function hexFor(name: string, o: ItemOptions | null): string | undefined {
  if (!o) return undefined;
  for (const f of o.color_families) {
    const hit = f.shades.find((s) => s.name.toLowerCase() === name.toLowerCase());
    if (hit) return hit.hex || undefined;
  }
  return undefined;
}

export const KIND_LABELS: Record<ItemKind, string> = {
  clothing: "Clothing", shoes: "Shoes", accessory: "Accessory",
};
